/* ==== band-a.js ==== */
/* ============================================================
   BAND A — Session 1 only.
   Sections 01–05: hero, cert strip, gap, problem, science.

   One job: the landing position for this band's in-page links —
   the gift row (→ #modes) and the hero rating (→ #reviews).

   Both are plain anchors and stay that way — with this file
   blocked or broken they still scroll, just less precisely. All
   this does is improve where they stop.

   Why it needs improving: the shared scroll-padding-top is 108px,
   which parks the *section box* below the header. But .section
   carries --section-y of up to 160px in its own padding, so the
   first line of copy ends up ~250px down and the landing reads as
   a screen of empty paper. Aiming at the section's first piece of
   content instead of its box puts the heading where the eye
   expects it.

   Deliberately not fixed in CSS: a scroll-margin-top on #modes
   would be Band A styling Band B's section, and would silently
   drift the day Session 2 changes that section's padding. This
   only governs what happens when *this* link is clicked.
   ============================================================ */
(() => {
'use strict';

/* Both selectors are Band A elements. The targets (#modes, #reviews) belong
   to Bands C and D — this reads them at click time and never styles them, so
   it cannot collide with those seats. A renamed target just falls through to
   the browser's own anchor jump. */
/* .reel__chip was the third selector here. The reel cards carry real video and
   a play button now, and the chip — which was an anchor to #buy — is gone, so
   the selector matched nothing. Dropped rather than left as a dead entry. */
const links = document.querySelectorAll('.gift[href^="#"], .hero__rating[href^="#"]');
if (!links.length) return;

/* Gap between the sticky header and the first line of the target. */
const BREATHING_ROOM = 28;

links.forEach((link) => link.addEventListener('click', (e) => {
  /* Let the browser handle anything that isn't a plain left click —
     new tab, new window, download, and so on. */
  if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

  const section = document.querySelector(link.getAttribute('href'));
  if (!section) return;   /* target renamed or removed — fall back to the anchor */

  /* The first real content inside the section, not the section box. */
  const anchorEl = section.querySelector('.label, h2') || section;
  const nav = document.querySelector('.nav');
  const navH = nav ? nav.getBoundingClientRect().height : 0;

  const top = anchorEl.getBoundingClientRect().top + scrollY - navH - BREATHING_ROOM;

  e.preventDefault();
  scrollTo({
    top: Math.max(0, Math.round(top)),
    behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
  });

  /* Keep the URL shareable without letting the hash change re-trigger the
     browser's own jump to the section box. */
  history.replaceState(null, '', link.getAttribute('href'));
}));

/* ---------- floating contact rail: stand down at the footer ----------
   The rail is a shortcut to contact. The footer is already contact — links,
   socials, policies — so the shortcut stops earning its place there, and on a
   short window it would otherwise sit on top of that content.

   Reading .foot from here is deliberate: it is observed, never styled or
   edited, so this cannot collide with whoever owns the footer. If the footer
   is ever renamed the rail simply stays visible, which is the safe failure. */
const rail = document.getElementById('frail');
const foot = document.querySelector('.foot');

if (rail && foot) {
  const setGone = (gone) => rail.classList.toggle('is-gone', gone);

  const io = new IntersectionObserver(([e]) => setGone(e.isIntersecting), { threshold: 0 });
  io.observe(foot);

  /* Same net app.js carries, for the same reason: IntersectionObserver does
     not fire in a hidden document, so a background-tab load that is already
     scrolled to the footer would leave the rail sitting over it. */
  function sweep() {
    const r = foot.getBoundingClientRect();
    setGone(r.top < innerHeight && r.bottom > 0);
  }
  addEventListener('visibilitychange', () => { if (!document.hidden) sweep(); });
  addEventListener('load', () => setTimeout(sweep, 1200));
}

})();

/* ============================================================
   §02b social reels — muted autoplay, one voice at most.

   The rail behaves the way a reel wall is expected to: cards
   play when they scroll into view and pause when they leave.
   They start muted, which is not a preference — a browser will
   not autoplay a card with sound, so muted is the only version
   of autoplay that exists.

   Sound is opt-in per card, and unmuting one mutes the other
   five. That is the whole reason autoplay is safe here: six
   cards can play at once because at most one of them is audible.

   Pausing on exit is not just tidiness. Six decoding video
   elements running off-screen is real battery and CPU on a
   phone, and preload="none" plus this observer means a card
   costs its poster until it is actually scrolled to.
   ============================================================ */
(() => {
'use strict';

const cards = [...document.querySelectorAll('.reel')];
if (!cards.length) return;

const items = cards.map((card) => ({
  card,
  video:  card.querySelector('[data-reel]'),
  play:   card.querySelector('[data-reel-toggle]'),
  sound:  card.querySelector('[data-reel-sound]')
})).filter((it) => it.video && it.play);

if (!items.length) return;

const reduced = matchMedia('(prefers-reduced-motion: reduce)');

/* Set when the reader pauses a card by hand. The observer must not then
   restart it the next time it crosses the viewport edge — an autoplay that
   overrides an explicit pause is the single most annoying thing this
   component can do. Cleared when they press play again. */
const held = new WeakSet();

function start(it) {
  if (held.has(it.video)) return;
  const p = it.video.play();
  /* play() rejects if the gesture heuristics say no, or the file is missing.
     Swallowed: the card stays on its poster rather than throwing on scroll. */
  if (p && typeof p.catch === 'function') p.catch(() => {});
}

function setSound(it, on) {
  it.video.muted = !on;
  if (!it.sound) return;
  it.sound.setAttribute('aria-pressed', on ? 'true' : 'false');
  it.sound.setAttribute('aria-label', on ? 'Mute video' : 'Unmute video');
}

items.forEach((it) => {
  const label = it.video.getAttribute('aria-label') || '';

  it.video.addEventListener('play', () => {
    it.card.classList.add('is-playing');
    it.play.setAttribute('aria-label', 'Pause video: ' + label);
  });
  const off = () => {
    it.card.classList.remove('is-playing');
    it.play.setAttribute('aria-label', 'Play video: ' + label);
  };
  it.video.addEventListener('pause', off);
  it.video.addEventListener('ended', off);

  const toggle = () => {
    if (it.video.paused) { held.delete(it.video); start(it); }
    else { held.add(it.video); it.video.pause(); }
  };
  it.play.addEventListener('click', toggle);
  /* The corner disc is the visible affordance; the frame itself is the big
     target. Tapping footage to pause it is the gesture every reel UI taught. */
  it.video.addEventListener('click', toggle);

  /* Controls chrome: shown by pointer activity, gone 3s after it stops.
     pointermove rather than pointerenter so the timer restarts while the
     pointer is alive on the card — the 3s counts from the LAST movement, the
     way every video player's chrome behaves. pointerdown covers touch, where
     a tap is the only activity there is. The CSS keeps a paused card's play
     disc and an unmuted card's speaker visible with the class gone. */
  let ctlT = 0;
  const chrome = () => {
    it.card.classList.add('ctl-on');
    clearTimeout(ctlT);
    ctlT = setTimeout(() => it.card.classList.remove('ctl-on'), 3000);
  };
  it.card.addEventListener('pointermove', chrome);
  it.card.addEventListener('pointerdown', chrome);
  it.card.addEventListener('pointerleave', () => {
    clearTimeout(ctlT);
    it.card.classList.remove('ctl-on');
  });

  if (it.sound) it.sound.addEventListener('click', () => {
    const turningOn = it.video.muted;
    /* At most one card is ever audible. */
    items.forEach((other) => setSound(other, false));
    if (turningOn) {
      setSound(it, true);
      /* Unmuting a card the reader can hear but not see is pointless — if it
         was paused, this is also a request to play it. */
      if (it.video.paused) { held.delete(it.video); start(it); }
    }
  });

  setSound(it, false);
});

/* rootMargin starts a card slightly before it is on screen so it is already
   running by the time it is looked at, rather than visibly kicking in.
   threshold 0.4 for the leaving edge: the rail is horizontal, so a card is
   still substantially visible when it reaches the edge, and pausing the
   moment it clips would stop a video that is still being watched. */
if ('IntersectionObserver' in window) {
  const io = new IntersectionObserver((entries) => {
    entries.forEach((e) => {
      const it = items.find((x) => x.card === e.target);
      if (!it) return;
      if (e.isIntersecting) start(it);
      else if (!it.video.paused) { setSound(it, false); it.video.pause(); }
    });
  }, { threshold: 0.4, rootMargin: '0px 120px 0px 120px' });
  items.forEach((it) => io.observe(it.card));
} else {
  /* No observer: leave every card on its poster rather than autoplaying six
     videos with nothing to stop them. The buttons still work. */
  items.forEach((it) => it.video.pause());
}

/* Motion reduced means do not start things by yourself. The controls stay,
   so the cards are all still playable — just never without being asked. */
if (reduced.matches) items.forEach((it) => { held.add(it.video); it.video.pause(); });

/* IntersectionObserver does not fire in a hidden document, so a tab switched
   away mid-playback would keep going — and keep talking, if one is unmuted.
   Same guard app.js carries. */
addEventListener('visibilitychange', () => {
  if (document.hidden) items.forEach((it) => it.video.pause());
});

})();

/* ============================================================
   "Book a demo" → the ChQMe video-call widget.

   ChQMe is a script embed that floats its own button on the live
   page (#chqme-widget-btn — the same element the .frail CSS makes
   room for). This routes the hero's demo CTA through that widget:
   one click, straight into the video-call flow it opens.

   Resolved at CLICK time, not load time: the widget script loads
   late and this file loads early — a load-time lookup would miss
   it. Where the widget is absent (localhost, blocked script), the
   anchor's own href is the fallback: the same prefilled WhatsApp
   demo thread the #buy section's demo button uses.
   ============================================================ */
(() => {
'use strict';

document.querySelectorAll('[data-demo-cta]').forEach((cta) => {
  cta.addEventListener('click', (e) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
    const widget = document.getElementById('chqme-widget-btn');
    if (!widget) return;              /* no widget → the href handles it */
    e.preventDefault();
    widget.click();
  });
});

})();

/* ============================================================
   nav mini cart — the slide-out behind the cart icon.

   Progressive enhancement over a real /cart link: with this file
   blocked, missing, or the AJAX cart unreachable (localhost has
   no Shopify behind it), the icon is still a working link to the
   cart page. Nothing here is load-bearing.

   Talks to Shopify's AJAX cart:
     GET  /cart.js          read
     POST /cart/change.js   set a line's quantity (0 = remove)
   Both are same-origin on the live store.

   Other bands: dispatch  new Event('hm:cart:open')  on document
   to open it (e.g. after an add-to-cart), and the drawer will
   re-fetch and show the fresh state.
   ============================================================ */
(() => {
'use strict';

const root   = document.getElementById('cartd');
const opener = document.querySelector('[data-cart-open]');
if (!root || !opener) return;

const panel  = root.querySelector('.cartd__panel');
const body   = root.querySelector('[data-cart-body]');
const foot   = root.querySelector('[data-cart-foot]');
const subEl  = root.querySelector('[data-cart-subtotal]');
const nEl    = root.querySelector('[data-cart-n]');
const badge  = document.querySelector('[data-cart-count]');

/* Money arrives in paise. Indian grouping, no paise shown when whole —
   which Shopify prices here always are. ₹ = ₹, kept as an escape so the
   file stays ASCII-safe wherever a host omits a charset. */
const money = (paise) => '₹' + (paise / 100).toLocaleString('en-IN', { maximumFractionDigits: 2, minimumFractionDigits: 0 });

const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

function paintBadge(count) {
  if (!badge) return;
  badge.hidden = !count;
  badge.textContent = count > 99 ? '99+' : String(count);
}

function render(cart) {
  paintBadge(cart.item_count);
  if (nEl) { nEl.hidden = !cart.item_count; nEl.textContent = '· ' + cart.item_count; }

  if (!cart.item_count) {
    body.innerHTML = '<p class="cartd__empty">Your cart is empty.</p>';
    foot.hidden = true;
    return;
  }

  body.innerHTML = cart.items.map((it) => `
    <div class="cartd__item" data-key="${esc(it.key)}">
      ${it.image ? `<img class="cartd__img" src="${esc(it.image)}" alt="" loading="lazy">` : '<span class="cartd__img"></span>'}
      <div>
        <p class="cartd__name">${esc(it.product_title)}</p>
        ${it.variant_title ? `<p class="cartd__variant">${esc(it.variant_title)}</p>` : ''}
        <span class="cartd__qty">
          <button type="button" data-q="${it.quantity - 1}" aria-label="Decrease quantity">&minus;</button>
          <span aria-label="Quantity">${it.quantity}</span>
          <button type="button" data-q="${it.quantity + 1}" aria-label="Increase quantity">+</button>
        </span>
      </div>
      <div>
        <p class="cartd__price">${money(it.final_line_price)}</p>
        <button type="button" class="cartd__rm" data-q="0">Remove</button>
      </div>
    </div>`).join('');

  subEl.textContent = money(cart.items_subtotal_price);
  foot.hidden = false;
}

async function fetchCart() {
  const r = await fetch('/cart.js', { credentials: 'same-origin' });
  if (!r.ok) throw new Error(r.status);
  return r.json();
}

async function setQty(key, q) {
  root.classList.add('is-busy');
  try {
    const r = await fetch('/cart/change.js', {
      method: 'POST', credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity: q })
    });
    if (!r.ok) throw new Error(r.status);
    render(await r.json());
  } catch {
    /* One failed write must not strand a stale drawer — re-read. */
    try { render(await fetchCart()); } catch { /* keep what's shown */ }
  } finally {
    root.classList.remove('is-busy');
  }
}

body.addEventListener('click', (e) => {
  const btn = e.target.closest('[data-q]');
  if (!btn) return;
  const item = btn.closest('[data-key]');
  if (item) setQty(item.dataset.key, Math.max(0, +btn.dataset.q));
});

/* ---- open / close ---- */
let restoreFocus = null;
let lastPad = '';

function lock() {
  /* The lock is inline (predates the porter's html.-passthrough fix; works,
     stays). The class is the CSS hook that hides the ChQMe embed while the
     drawer is open — its z-index outranks the scrim. See the CSS note. */
  const sbw = innerWidth - document.documentElement.clientWidth;
  lastPad = document.body.style.paddingRight;
  document.documentElement.style.overflow = 'hidden';
  if (sbw > 0) document.body.style.paddingRight = sbw + 'px';
  document.documentElement.classList.add('cartd-open');
}
function unlock() {
  document.documentElement.style.overflow = '';
  document.body.style.paddingRight = lastPad;
  document.documentElement.classList.remove('cartd-open');
}

function open() {
  restoreFocus = document.activeElement;
  root.classList.add('open');
  root.setAttribute('aria-hidden', 'false');
  lock();
  root.querySelector('.cartd__close').focus();
  fetchCart().then(render).catch(() => {
    /* No AJAX cart here (localhost, or Shopify hiccup). The link the click
       came from is the fallback — follow it. */
    close();
    location.href = opener.href;
  });
}

function close() {
  if (!root.classList.contains('open')) return;
  root.classList.remove('open');
  root.setAttribute('aria-hidden', 'true');
  unlock();
  if (restoreFocus && document.contains(restoreFocus)) restoreFocus.focus();
}

opener.addEventListener('click', (e) => {
  if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
  e.preventDefault();
  open();
});
document.addEventListener('hm:cart:open', open);

root.addEventListener('click', (e) => {
  if (e.target.closest('[data-cart-close]')) close();
});
addEventListener('keydown', (e) => {
  if (e.key === 'Escape') close();
  /* Minimal focus trap: Tab cycles inside the panel while it is open. */
  if (e.key === 'Tab' && root.classList.contains('open')) {
    const f = [...panel.querySelectorAll('a[href], button:not([disabled])')].filter((el) => el.offsetParent !== null);
    if (!f.length) return;
    const first = f[0], last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }
});

/* Badge on load: one small same-origin read. On localhost it fails and the
   badge simply stays hidden. Deferred so it never competes with the videos
   and images this page is actually about. */
if ('requestIdleCallback' in window) requestIdleCallback(() => fetchCart().then(render).catch(() => {}));
else setTimeout(() => fetchCart().then(render).catch(() => {}), 2500);

})();

/* ============================================================
   §01 hero gallery — arrows, thumbnails, and the active state.

   A separate IIFE on purpose. The block above returns early when
   it finds no in-band anchors, and anything appended inside it
   would inherit that early return and silently never run.

   What this file does NOT do is the swiping. The track is a
   scroll-snap container, so touch, trackpad and shift-wheel are
   the platform's own and already work with this file blocked —
   all nine slides remain reachable. This adds the arrows, the
   thumbnail jumps, and the reading-back of scroll position into
   which thumb is lit. Every control here is a real <button>, so
   the failure mode is "the buttons do nothing", not "the gallery
   is unusable".
   ============================================================ */
(() => {
'use strict';

const root = document.querySelector('[data-gal]');
if (!root) return;

const track  = root.querySelector('[data-gal-track]');
const slides = [...root.querySelectorAll('[data-gal-slide]')];
const thumbs = [...root.querySelectorAll('[data-gal-to]')];
if (!track || slides.length < 2) return;

const reduced = matchMedia('(prefers-reduced-motion: reduce)');
const prev = root.querySelector('[data-gal-nav="prev"]');
const next = root.querySelector('[data-gal-nav="next"]');
const video = root.querySelector('.gal__video');

/* Derived from the live scroll position rather than stored in a variable.
   A swipe, an arrow, a thumb and a browser-restored scroll offset all move
   the same number, so reading it is the only way the four cannot disagree. */
function current() {
  const w = track.clientWidth;
  if (!w) return 0;
  return Math.max(0, Math.min(slides.length - 1, Math.round(track.scrollLeft / w)));
}

/* Wraps: one past the end is the first slide, one before the start is the
   last (user call, 2026-08-15). An arrow that dies at the last slide reads
   as a broken button, and the natural gesture at the end of a carousel is
   "keep going". Only the ARROWS wrap — the swipe is native scroll-snap and
   clamps at the ends like every native carousel, which is what fingers
   expect. The wrap rides the same smooth scroll as any other jump: a fast
   visible rewind, not a teleport that looks like a glitch. */
function go(i) {
  const n = ((i % slides.length) + slides.length) % slides.length;
  track.scrollTo({
    left: n * track.clientWidth,
    behavior: reduced.matches ? 'auto' : 'smooth'
  });
}

function paint() {
  const i = current();

  thumbs.forEach((t, n) => {
    const on = n === i;
    t.classList.toggle('is-on', on);
    t.setAttribute('aria-selected', on ? 'true' : 'false');
  });

  /* Arrows never retire: the carousel loops, so there is no "end" for them
     to die at. (.gal__nav.is-off in the CSS is now unused, kept for the day
     looping is reverted.) */

  /* Keep the lit thumb in view when the rail is scrolling at narrow widths.
     Guarded on the rail actually overflowing: on desktop all nine fit, and
     calling this unconditionally scrolled the *page* to the hero on first
     paint in Safari, because scrollIntoView walks every scrollable ancestor
     when the nearest one has nothing to scroll. */
  const rail = thumbs[i] && thumbs[i].parentElement;
  if (rail && rail.scrollWidth > rail.clientWidth) {
    const t = thumbs[i].getBoundingClientRect();
    const r = rail.getBoundingClientRect();
    if (t.left < r.left || t.right > r.right) {
      rail.scrollTo({
        left: thumbs[i].offsetLeft - (rail.clientWidth - thumbs[i].offsetWidth) / 2,
        behavior: reduced.matches ? 'auto' : 'smooth'
      });
    }
  }

  /* Slide 1 is the only one that can be making noise. Leaving it playing
     while the reader has swiped to a photo means audio from something that
     is no longer on screen. Pause, never auto-play: starting it is the
     reader's decision and stays theirs. */
  if (video && i !== 0 && !video.paused) video.pause();
}

/* Stands the PPB card down while the video is playing — it otherwise covers
   the left half of the control bar and makes the scrubber unclickable. Bound to
   play/pause rather than to the slide index so the card is present on load,
   which is when the video slide is showing and nothing is playing yet. */
if (video) {
  const playing = (on) => root.classList.toggle('is-playing', on);
  video.addEventListener('play',  () => playing(true));
  video.addEventListener('pause', () => playing(false));
  video.addEventListener('ended', () => playing(false));
}

if (prev) prev.addEventListener('click', () => go(current() - 1));
if (next) next.addEventListener('click', () => go(current() + 1));
thumbs.forEach((t, n) => t.addEventListener('click', () => go(n)));

/* Left/right arrows move the gallery when focus is inside the thumbnail rail,
   which is what role="tablist" promises. */
thumbs.forEach((t, n) => t.addEventListener('keydown', (e) => {
  if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
  e.preventDefault();
  /* Same wrap as the arrows — right from the last thumb lands on the first. */
  const to = (n + (e.key === 'ArrowRight' ? 1 : -1) + thumbs.length) % thumbs.length;
  thumbs[to].focus();
  go(to);
}));

/* Scroll fires continuously through a swipe; one rAF-gated paint per frame is
   enough to keep the thumbs in step without doing layout work per event. */
let queued = false;
track.addEventListener('scroll', () => {
  if (queued) return;
  queued = true;
  requestAnimationFrame(() => { queued = false; paint(); });
}, { passive: true });

/* clientWidth is the slide width, so a resize invalidates the scroll offset —
   without this, a rotate or a window drag leaves the track parked between two
   slides. Re-snapping to the slide that was showing keeps the gallery honest.
   Read the index BEFORE the width changes take effect on the next frame, and
   jump without animation: this is a correction, not a navigation. */
addEventListener('resize', () => {
  const i = current();
  requestAnimationFrame(() => {
    track.scrollTo({ left: i * track.clientWidth, behavior: 'auto' });
    paint();
  });
});

paint();

})();

/* ═══════════════ §01 · live delivery date ═══════════════
   Replaces the static "Delhi NCR same-day · Metros 2 days · Rest of India
   4-5 days" with a real Shiprocket estimate for the reader's own pincode.

   Two ways in, in this order:
     1. On load, ask the relay with no pincode. It reads the visitor's
        location from the edge and answers for them. Nobody types anything.
     2. A "Change" button opens a pincode box for anyone the guess missed
        (VPN, office IP, buying for someone else, or plain curiosity).

   The static promise is what is in the HTML, and it is never cleared before
   a real answer arrives — no spinner, no empty state, no layout jump in the
   middle of the buy column. Every failure path simply leaves it standing.

   The typed pincode is remembered, because the reader who corrected our
   guess once should not have to correct it on every visit. */
(() => {
  'use strict';

  const box = document.querySelector('[data-eta]');
  if (!box) return;

  const endpoint = box.dataset.etaEndpoint;
  const out      = box.querySelector('[data-eta-out]');
  const text     = box.querySelector('[data-eta-text]');
  const form     = box.querySelector('[data-eta-form]');
  const input    = box.querySelector('[data-eta-input]');
  if (!endpoint || !out || !text || !form || !input) return;

  /* The form is hidden in the HTML because with JS off it would submit to the
     current URL and reload the page. This script IS the JS, so revealing it
     here is the whole gate — and it happens on init, not after a response:
     someone who wants to type their own pincode should never have to wait on
     a guess about them. */
  form.hidden = false;

  const KEY = 'adivaa:pin';
  const PIN = /^[1-9]\d{5}$/;
  const store = {
    get() { try { return localStorage.getItem(KEY); } catch (_) { return null; } },
    set(v) { try { localStorage.setItem(KEY, v); } catch (_) {} },
  };

  /* Local midnight, not UTC. `new Date("2026-08-19")` is parsed as UTC and
     shown in local time, which reads as the 18th anywhere west of Greenwich
     — a delivery promise one day early. Build the date from its parts. */
  function localDate(iso) {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso || '');
    return m ? new Date(+m[1], +m[2] - 1, +m[3]) : null;
  }

  const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const MONS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  /* "today" / "tomorrow" / "Wed, 20 Aug". Counted off the READER's clock, so
     a date that has already passed (a stale edge cache over midnight) can
     never render as a future promise — it falls through to the plain date. */
  function when(iso) {
    const d = localDate(iso);
    if (!d) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const diff = Math.round((d - today) / 86400000);
    if (diff <= 0) return 'today';
    if (diff === 1) return 'tomorrow';
    return `${DAYS[d.getDay()]}, ${d.getDate()} ${MONS[d.getMonth()]}`;
  }

  const esc = (s) => String(s).replace(/[&<>"]/g, (c) => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]
  ));

  /* City and state both come from Shiprocket, so both are escaped before
     they touch innerHTML — an upstream field is not our text.

     The city shows ONLY for a pincode the reader gave us. On the IP guess it
     is noise at best and an accusation at worst: naming a city they are not
     in reads as "we think you are in Raigarh" and invites them to distrust
     the date too. The date is the useful half and it is right either way,
     because it comes from the pincode, not from the name we put on it. */
  function render(d, withCity) {
    const label = when(d.date);
    if (!label) return false;
    /* No leading space inside the second span. .qship__out is a flex row with
       its own gap, so a literal space here rendered as gap PLUS space and the
       city drifted away from the date it belongs to. The gap is the spacing.

       The LINE is the control — there is no separate "Change" button. Two
       reasons. It was a sixth element in a row that exists to say one thing,
       and at 1440x900 it landed at x1330-1376 with the WhatsApp rail fixed
       from x1360: the one interactive thing in the row sat under a floating
       widget and could not be clicked. A whole-line target cannot be covered
       by a 54px bubble at the end of it. */
    /* One innerHTML on one span, so every gap in the sentence is ordinary
       word spacing. The date and the city used to be separate flex items,
       which put a flex gap on one side of "to" and a text space on the
       other — they never matched at any size. */
    const where = withCity && d.city ? ` to <b>${esc(d.city)}</b>` : '';
    text.innerHTML = `Arrives <b>${label}</b>${where}`;
    box.classList.add('is-live');
    return true;
  }

  let busy = false;

  async function ask(pin) {
    if (busy) return;
    busy = true;
    box.classList.add('is-busy');
    try {
      const url = endpoint + (pin ? `?pin=${encodeURIComponent(pin)}` : '');
      const r = await fetch(url, { headers: { Accept: 'application/json' } });
      const d = await r.json();

      if (d && d.ok && render(d, Boolean(pin))) {
        if (pin) store.set(pin);
        box.classList.remove('is-bad');
      } else if (pin && d && (d.reason === 'unserviceable' || d.reason === 'badpin')) {
        /* Only a pincode the reader typed earns an error — a failed guess
           they never asked for must stay silent. And only a real "no" earns
           it: a 502 {reason:"upstream"} means Shiprocket (or our login to
           it) is down, which says nothing about the pincode. Showing "We do
           not deliver there yet" for that told a Palghar buyer we don't ship
           to them during an outage (2026-09-12). Upstream failures keep the
           static promise, like every other failure path. */
        box.classList.add('is-bad');
        input.focus();
        input.select();
      }

      return Boolean(d && d.ok);
    } catch (_) {
      return false;                      /* static promise stands */
    } finally {
      busy = false;
      box.classList.remove('is-busy');
    }
  }

  /* The field is always there now, so there is nothing to open or close and
     the reader's own pincode is one keystroke away at all times. */
  input.value = store.get() || '';

  form.addEventListener('submit', (e) => {
    e.preventDefault();
    const pin = input.value.trim();
    if (!PIN.test(pin)) { box.classList.add('is-bad'); input.focus(); return; }
    ask(pin);
  });
  /* Typing is the reader correcting themselves; drop the error with the
     first keystroke rather than making them submit to clear it. */
  input.addEventListener('input', () => box.classList.remove('is-bad'));
  /* A pincode they gave us before beats a fresh IP guess: it is the one
     they already told us was right. */
  const saved = store.get();
  ask(PIN.test(saved || '') ? saved : null);
})();

/* ============================================================
   §02c unboxing film — functional copy of the reference player
   (pb-mob-unboxing-video.liquid), re-set in this page's dialect.

   The logic is the reference's, hook for hook: an intro overlay
   that any tap dismisses into playback; a bottom bar (time,
   seekable timeline, back 10 / play / forward 10 / mute /
   fullscreen) that lives only while .is-playing and goes dark
   3s after the last interaction (.ctl-off); a tap surface where
   a double tap on a side third skips 10s behind a half-moon
   flash, a double tap in the middle toggles, and a lone tap
   toggles after the 300ms window; a paused-state brand disc.

   Two deliberate departures, both flagged in the markup comment:
   the film starts unmuted (it only ever starts from a tap, so
   sound is allowed and the mute button takes it away), and the
   page's one-voice law is kept: starting the film releases any
   unmuted reel through the reel's own button, and unmuting a
   reel pauses the film.
   ============================================================ */
(() => {
'use strict';

const box = document.querySelector('[data-unbox]');
if (!box) return;

const video = box.querySelector('[data-unbox-video]');
if (!video) return;

const $ = (sel) => box.querySelector(sel);
const skipL   = $('[data-unbox-skip="l"]');
const skipR   = $('[data-unbox-skip="r"]');
const center  = $('[data-unbox-center]');
const cIco    = $('[data-unbox-center-ico]');
const bigplay = $('[data-unbox-bigplay]');
const intro   = $('[data-unbox-intro]');
const timeEl  = $('[data-unbox-time]');
const tl      = $('[data-unbox-tl]');
const backBtn = $('[data-unbox-back]');
const fwdBtn  = $('[data-unbox-fwd]');
const playBtn = $('[data-unbox-play]');
const pIco    = $('[data-unbox-play-ico]');
const muteBtn = $('[data-unbox-mute]');
const mIco    = $('[data-unbox-mute-ico]');
const fsBtn   = $('[data-unbox-fs]');
const fsIco   = $('[data-unbox-fs-ico]');

const PLAY_PATH  = 'M8 5v14l11-7z';
const PAUSE_PATH = 'M6 19h4V5H6v14zm8-14v14h4V5h-4z';
const MUTE_PATH  = 'M16.5 12c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77zM3 9v6h4l5 5V4L7 9H3z';
const UNMUTE_PATH = 'M16.5 12c0-1.77-1.02-3.29-2.5-4.03v1.79l2.48 2.48c.01-.08.02-.16.02-.24zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3L4.27 3zM12 4L9.91 6.09 12 8.18V4z';
const FS_PATH      = 'M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z';
const EXIT_FS_PATH = 'M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z';

/* ---- auto-hide: the bar dies 3s after the last interaction ---- */
let hideT = 0;
function resetHideTimer() {
  box.classList.remove('ctl-off');
  clearTimeout(hideT);
  if (!video.paused) {
    hideT = setTimeout(() => box.classList.add('ctl-off'), 3000);
  }
}
box.addEventListener('mousemove', resetHideTimer);
box.addEventListener('touchstart', resetHideTimer, { passive: true });
box.addEventListener('mouseenter', resetHideTimer);
box.addEventListener('click', resetHideTimer);

function fmt(s) {
  if (!Number.isFinite(s)) s = 0;
  const m = Math.floor(s / 60), r = Math.floor(s % 60);
  return m + ':' + String(r).padStart(2, '0');
}

/* ---- feedback flashes ---- */
function flash(el, ms) {
  if (!el) return;
  el.classList.remove('is-on');
  void el.offsetWidth;                     /* restart the keyframe */
  el.classList.add('is-on');
  setTimeout(() => el.classList.remove('is-on'), ms);
}
function centerFeedback(isPlay) {
  if (cIco) cIco.setAttribute('d', isPlay ? PLAY_PATH : PAUSE_PATH);
  flash(center, 600);
}

/* ---- play / pause core ---- */
function start() {
  /* One voice on the page: starting the film silences any unmuted reel,
     through the reel's own button so its aria state stays honest. */
  document.querySelectorAll('[data-reel-sound][aria-pressed="true"]')
    .forEach((b) => b.click());
  const p = video.play();
  if (p && typeof p.catch === 'function') p.catch(() => {});
}
function togglePlay() {
  if (video.paused) { centerFeedback(true); start(); }
  else { centerFeedback(false); video.pause(); }
}

/* class + icon state rides the media events, so every path that starts or
   stops the film (buttons, taps, the observer) lands in one place */
video.addEventListener('play', () => {
  box.classList.add('is-playing');
  box.classList.remove('is-paused');
  if (intro) intro.classList.add('is-hidden');
  if (pIco) pIco.setAttribute('d', PAUSE_PATH);
  if (playBtn) playBtn.setAttribute('aria-label', 'Pause');
  resetHideTimer();
});
video.addEventListener('pause', () => {
  box.classList.add('is-paused');
  if (pIco) pIco.setAttribute('d', PLAY_PATH);
  if (playBtn) playBtn.setAttribute('aria-label', 'Play');
  box.classList.remove('ctl-off');
  clearTimeout(hideT);
});

/* ---- the tap surface: single = toggle, double on a third = skip ---- */
function skip(dir, feedback) {
  const dur = video.duration;
  const cap = Number.isFinite(dur) ? dur : Number.MAX_SAFE_INTEGER;
  video.currentTime = Math.max(0, Math.min(cap, video.currentTime + dir * 10));
  if (feedback) flash(dir < 0 ? skipL : skipR, 1000);
}

let lastTap = 0, tapT = 0;
box.addEventListener('click', (e) => {
  /* the controls and the discs answer for themselves */
  if (e.target.closest('.unbox__btn') || e.target.closest('.unbox__tl') ||
      e.target.closest('[data-unbox-bigplay]') || e.target.closest('[data-unbox-intro]')) return;

  const now = Date.now();
  const gap = now - lastTap;

  if (gap < 300 && gap > 0) {
    clearTimeout(tapT);
    const r = box.getBoundingClientRect();
    const x = e.clientX - r.left;
    if (x < r.width * 0.35)      skip(-1, true);
    else if (x > r.width * 0.65) skip(1, true);
    else togglePlay();
    lastTap = 0;
    return;
  }
  lastTap = now;
  tapT = setTimeout(() => { togglePlay(); resetHideTimer(); }, 300);
});

/* ---- the intro overlay and the paused disc ---- */
if (intro) intro.addEventListener('click', (e) => {
  e.preventDefault(); e.stopPropagation();
  start();
});
if (bigplay) bigplay.addEventListener('click', (e) => {
  e.stopPropagation();
  togglePlay();
});

/* ---- control bar ---- */
if (playBtn) playBtn.addEventListener('click', (e) => { e.stopPropagation(); togglePlay(); });
if (backBtn) backBtn.addEventListener('click', (e) => { e.stopPropagation(); skip(-1, false); });
if (fwdBtn)  fwdBtn.addEventListener('click',  (e) => { e.stopPropagation(); skip(1, false); });

function setMuteIcon() {
  if (mIco) mIco.setAttribute('d', video.muted ? UNMUTE_PATH : MUTE_PATH);
  if (muteBtn) muteBtn.setAttribute('aria-label', video.muted ? 'Unmute' : 'Mute');
}
if (muteBtn) muteBtn.addEventListener('click', (e) => {
  e.stopPropagation();
  video.muted = !video.muted;
  setMuteIcon();
});
setMuteIcon();

/* timeline: input seeks, timeupdate paints the fill in 0.5% steps */
if (tl) {
  video.addEventListener('loadedmetadata', () => {
    tl.max = video.duration;
    if (timeEl) timeEl.textContent = fmt(0) + ' / ' + fmt(video.duration);
  });
  let lastP = -1;
  video.addEventListener('timeupdate', () => {
    if (timeEl) timeEl.textContent = fmt(video.currentTime) + ' / ' + fmt(video.duration);
    if (!Number.isFinite(video.duration) || !video.duration) return;
    const p = Math.round((video.currentTime / video.duration) * 200) / 2;
    if (p !== lastP) {
      lastP = p;
      tl.value = video.currentTime;
      requestAnimationFrame(() => tl.style.setProperty('--unbox-progress', p + '%'));
    }
  });
  tl.addEventListener('input', () => { video.currentTime = tl.value; });
  /* keep a drag on the thumb from reading as a tap on the surface */
  tl.addEventListener('mousedown',  (e) => e.stopPropagation());
  tl.addEventListener('touchstart', (e) => e.stopPropagation(), { passive: true });
}

/* fullscreen on the card, icon follows the document state */
if (fsBtn) {
  const fsEl = () => document.fullscreenElement || document.webkitFullscreenElement || document.msFullscreenElement;
  const updateIco = () => {
    if (fsIco) fsIco.setAttribute('d', fsEl() ? EXIT_FS_PATH : FS_PATH);
    if (fsBtn) fsBtn.setAttribute('aria-label', fsEl() ? 'Exit full screen' : 'Full screen');
  };
  fsBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    if (!fsEl()) {
      if (box.requestFullscreen) box.requestFullscreen();
      else if (box.webkitRequestFullscreen) box.webkitRequestFullscreen();
      else if (video.webkitEnterFullscreen) video.webkitEnterFullscreen(); /* iPhone: element fullscreen does not exist */
    } else {
      if (document.exitFullscreen) document.exitFullscreen();
      else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
    }
  });
  ['fullscreenchange', 'webkitfullscreenchange', 'MSFullscreenChange'].forEach((ev) =>
    document.addEventListener(ev, updateIco));
}

/* keyboard, because the card is tabbable */
box.addEventListener('keydown', (e) => {
  if (e.target !== box) return;               /* buttons handle their own keys */
  if (e.key === ' ' || e.key === 'k')      { e.preventDefault(); togglePlay(); }
  else if (e.key === 'ArrowLeft')          { e.preventDefault(); skip(-1, true); }
  else if (e.key === 'ArrowRight')         { e.preventDefault(); skip(1, true); }
  else if (e.key === 'm' && muteBtn)       { e.preventDefault(); muteBtn.click(); }
});

/* a reel being unmuted pauses the film (bubble phase, so the reel's own
   handler has already settled the button's state) */
document.addEventListener('click', (e) => {
  const b = e.target && e.target.closest ? e.target.closest('[data-reel-sound]') : null;
  if (b && b.getAttribute('aria-pressed') === 'true' && !video.paused) video.pause();
});

/* off screen is paused; the reader restarts it, nothing else does */
if ('IntersectionObserver' in window) {
  const io = new IntersectionObserver((entries) => {
    entries.forEach((en) => {
      if (!en.isIntersecting && !video.paused) video.pause();
    });
  }, { threshold: 0.25 });
  io.observe(video);
}

})();

/* ▼ Dhanteras gift (2026-10-24) ▼ ===========================================
   The Diwali box's clock, stock and end ([data-dw-end] on each box; on the
   combined page there is one per gift model). The porter renders
   data-dw-stock from HydroGlow's live inventory. At the deadline or at zero
   stock the offer ends on the page by itself: the boxes and gift chips hide,
   the plain chips come back, and every data-gift-variant goes, so band-d.js
   puts nothing in the cart. While it runs, the delivery line gets "before
   Dhanteras" whenever the quoted date lands by the deadline. Cut into
   HydroGo's page by tools/seed-hydrogo.py, so keep the markers.
   ========================================================================== */
(() => {
  'use strict';

  const boxes = [...document.querySelectorAll('[data-dw-end]')];
  if (!boxes.length) return;

  const end = Date.parse(boxes[0].dataset.dwEnd);
  const stock = Number(boxes[0].dataset.dwStock);
  const start = Number(boxes[0].dataset.dwStart) || stock;
  let over = false;

  const each = (sel, fn) => document.querySelectorAll(sel).forEach(fn);
  function finish() {
    over = true;
    each('[data-dw-end], [data-dw-chip], .eta-dw', (el) => { el.style.display = 'none'; });
    each('[data-dw-after]', (el) => { el.style.display = ''; });
    each('[data-gift-variant]', (el) => el.removeAttribute('data-gift-variant'));
  }
  if (!(stock > 0) || !(end > Date.now())) { finish(); return; }

  boxes.forEach((b) => {
    b.querySelectorAll('[data-dw-n]').forEach((el) => { el.textContent = stock; });
    b.querySelectorAll('[data-dw-word]').forEach((el) => { el.textContent = stock === 1 ? 'HydroGlow' : 'HydroGlows'; });
    b.querySelectorAll('[data-dw-bar]').forEach((el) => { el.style.width = `${Math.max(4, Math.min(100, (stock / start) * 100))}%`; });
  });

  const pad = (n) => String(n).padStart(2, '0');
  function tick() {
    const left = end - Date.now();
    if (left <= 0) { finish(); return; }
    const mins = Math.floor(left / 60000);
    const parts = { d: Math.floor(mins / 1440), h: Math.floor(mins / 60) % 24, m: mins % 60 };
    boxes.forEach((b) => {
      Object.keys(parts).forEach((k) => b.querySelectorAll(`[data-dw-${k}]`).forEach((el) => { el.textContent = pad(parts[k]); }));
    });
    setTimeout(tick, (left % 60000) + 50);
  }
  tick();

  /* The delivery line. band-a.js rewrites it whenever a date comes back
     ("Arrives <b>Sat, 31 Oct</b>", "today", "tomorrow"; a pre-order says
     "Ships … · arrives …"), so watch it and re-add the tag each time. The
     tag carries the boxes' data-m, so on the combined page it hides with
     them when a bottle without the gift is selected. */
  const eta = document.querySelector('[data-eta-text]');
  if (!eta) return;
  const MONS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const models = [...new Set(boxes.flatMap((b) => (b.dataset.m || '').split(' ').filter(Boolean)))].join(' ');
  function arrives(txt) {
    const t = txt.split(/rrives/i).pop();
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    if (/\btoday\b/i.test(t)) return d;
    if (/\btomorrow\b/i.test(t)) { d.setDate(d.getDate() + 1); return d; }
    const m = new RegExp(`(\\d{1,2}) (${MONS.join('|')})`).exec(t);
    if (!m) return null;
    const out = new Date(d.getFullYear(), MONS.indexOf(m[2]), Number(m[1]));
    if (out < d) out.setFullYear(out.getFullYear() + 1);
    return out;
  }
  function tag() {
    if (over || eta.querySelector('.eta-dw')) return;
    const d = arrives(eta.textContent);
    if (!d || d.getTime() > end) return;
    const s = document.createElement('span');
    s.className = 'eta-dw';
    if (models) s.dataset.m = models;
    s.innerHTML = '<i> · </i>✓ before Dhanteras';
    eta.append(s);
  }
  new MutationObserver(tag).observe(eta, { childList: true, characterData: true, subtree: true });
  tag();
})();
/* ▲ Dhanteras gift ▲ */
